import { initSentry } from "@/lib/sentry";

initSentry();

import { StrictMode } from "react";

import { I18nProvider } from "@lingui/react";
import { PostHogProvider } from "@pontimed/analytics/client";
import { configureAuthClient } from "@pontimed/auth-client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { LazyMotion, MotionConfig, domAnimation } from "framer-motion";
import { Provider as JotaiProvider } from "jotai";
import { createRoot } from "react-dom/client";

import { hydrateInitialLocale, i18n } from "@/i18n/i18n";

import { routeTree } from "./routeTree.gen";

import "@/styles/globals.css";

/**
 * Recover from stale lazy chunks after a deploy — when a hashed code-split
 * chunk no longer exists (a newer build replaced it), the dynamic `import()`
 * 404s and Vite fires `vite:preloadError`. Reload ONCE to pull the fresh shell.
 * sessionStorage guard prevents reload loops. (Same as apps/app.)
 */
window.addEventListener("vite:preloadError", (event) => {
  const KEY = "pm:preload-reload-at";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return;
  event.preventDefault();
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

/**
 * Resolve apps/api base URL. In dev, the Vite proxy forwards `/api` →
 * :3200 so empty string works; in prod we hit api.pontimed.app directly.
 */
const API_BASE_URL = import.meta.env.NEXT_PUBLIC_API_URL ?? "";

/** PostHog config — admin staff are bound by employment contract so no
 *  cookie consent banner is mounted (see `_docs/posthog-setup.md`). The
 *  provider still gates init on the cookie because the consent helper is
 *  cross-app shared; staff can flip the cookie manually in dev if they
 *  need to opt out for a session. */
const POSTHOG_KEY =
  import.meta.env.NEXT_PUBLIC_POSTHOG_KEY ?? import.meta.env.VITE_POSTHOG_KEY ?? undefined;
const POSTHOG_HOST =
  import.meta.env.NEXT_PUBLIC_POSTHOG_HOST ?? import.meta.env.VITE_POSTHOG_HOST ?? undefined;

configureAuthClient({
  apiBaseUrl: API_BASE_URL,
  onAuthFailure: () => {
    // Defer redirect to AdminShell — atoms get cleared, _authenticated route
    // notices `unauthenticated` status and `navigate` to /login.
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("#root not found");

void hydrateInitialLocale().finally(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <JotaiProvider>
        <I18nProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            {/* Internal staff sessions = no autocapture noise. Every
                meaningful admin action is captured explicitly per call
                site. Recording stays at 100% (recordingSampleRate=1) so
                the audit trail captures every staff session. */}
            <PostHogProvider
              apiKey={POSTHOG_KEY}
              apiHost={POSTHOG_HOST}
              debug={import.meta.env.DEV}
              bypassConsent
              appName="admin"
              environment={import.meta.env.DEV ? "dev" : "prod"}
              disableAutocapture
              recordingSampleRate={1}
            >
              <MotionConfig reducedMotion="user">
                <LazyMotion features={domAnimation} strict>
                  <RouterProvider router={router} />
                </LazyMotion>
              </MotionConfig>
            </PostHogProvider>
          </QueryClientProvider>
        </I18nProvider>
      </JotaiProvider>
    </StrictMode>,
  );
});
